import { SERVER_URL } from '../config.js';
import { storage } from './storage.js';

/**
 * Handle URL parameters for tracking and preferences
 */
export class URLParamHandler {
  /**
   * Process URL parameters for tracking and preferences
   */
  static processParams() {
    const params = new URLSearchParams(window.location.search);

    // Handle via param for traffic tracking
    this.handleViaParam(params);

    // Handle pref param for gender preferences
    this.handlePrefParam(params);

    // Clean up processed params from URL
    this.cleanupProcessedParams(params);
  }

  /**
   * Handle via param for traffic tracking
   * @param {URLSearchParams} params
   */
  static handleViaParam(params) {
    const viaParam = params.get('via');
    if (viaParam) {
      fetch(`${SERVER_URL}/api/v1/track-traffic`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ via: viaParam })
      }).catch(error => {
        console.warn('[URLParamHandler] Failed to track traffic:', error);
      });
    }
  }

  /**
   * Handle pref param for setting gender preferences
   * Format: ?pref=X or ?pref=X-Y where X is gender (0=female, 1=male)
   * Second part (Y) is ignored (legacy param)
   * @param {URLSearchParams} params
   */
  static handlePrefParam(params) {
    const prefParam = params.get('pref');
    if (!prefParam) return;

    try {
      const [genderStr] = prefParam.split('-');
      const gender = parseInt(genderStr, 10);

      if (isNaN(gender) || (gender !== 0 && gender !== 1)) {
        console.warn('[URLParamHandler] Invalid pref param format:', prefParam);
        return;
      }

      // Set gender preference (0 = female, 1 = male)
      const isMale = gender === 1;
      storage.set('gender_preference', isMale ? 'true' : 'false');

      // Dispatch event to notify components
      window.dispatchEvent(new CustomEvent('genderPreferenceSelected', {
        detail: { isMale }
      }));

      console.log(`[URLParamHandler] Set preferences: gender=${isMale ? 'male' : 'female'}`);

    } catch (error) {
      console.warn('[URLParamHandler] Failed to parse pref param:', prefParam, error);
    }
  }

  /**
   * Extract session parameters for analytics tracking
   * Returns { via: string|null, gender: number|null }
   */
  static extractSessionParams() {
    const params = new URLSearchParams(window.location.search);

    let via = null;
    let gender = null;

    // Extract via param
    const viaParam = params.get('via');
    if (viaParam) {
      via = viaParam;
    }

    // Extract pref param (gender format)
    const prefParam = params.get('pref');
    if (prefParam) {
      try {
        const [genderStr] = prefParam.split('-');
        const parsedGender = parseInt(genderStr, 10);

        if (!isNaN(parsedGender) && (parsedGender === 0 || parsedGender === 1)) {
          gender = parsedGender;
        }
      } catch (error) {
        console.warn('[URLParamHandler] Failed to parse pref param for session tracking:', prefParam, error);
      }
    }

    return { via, gender };
  }

  /**
   * Remove processed params from URL
   * @param {URLSearchParams} params
   */
  static cleanupProcessedParams(params) {
    const paramsToRemove = ['via', 'pref'];
    let shouldUpdateUrl = false;

    paramsToRemove.forEach(param => {
      if (params.has(param)) {
        params.delete(param);
        shouldUpdateUrl = true;
      }
    });

    if (shouldUpdateUrl) {
      const newUrl = new URL(window.location.href);
      // Clear search params and set only the remaining ones
      newUrl.search = params.toString();

      window.history.replaceState({}, document.title, newUrl.toString());
    }
  }
}
